/*
 * RETIRED FROM THE SHIFT SHEET — UC-A (2 Oct 2026). Phase 12 retirement candidate; kept, not deleted.
 * Reason: UI-05/UI-10 (billing readiness is reviewed on the Billing review page, not in the roster sheet — Final PRD §33–34).
 * Former caller: components/calendar/CalendarShiftDrawer.jsx (pre-UC-A). Remaining caller (out of UC-A scope, unchanged): pages/roster/RosterCalendarPage.jsx.
 */
/**
 * SilBillingReadinessPanel
 * ─────────────────────────────────────────────────────────────────────────────
 * SIL Phase 3 — per-participant billing readiness (admin action).
 *
 * Shows a PER_PARTICIPANT SIL shift's per-resident allocations and their billing
 * readiness. For allocations still in PENDING_BILLING_CONFIGURATION it offers a
 * "Mark ready for billing" action that calls ONLY the existing backend endpoint:
 *   POST /api/shifts/{shift_id}/service-allocations/{allocation_id}/mark-ready
 *
 * Invariants:
 *  - The frontend NEVER decides readiness itself. The backend resolver (steps
 *    1–7) is the sole authority; this panel just renders its result.
 *  - On failure the backend's resolver reason is displayed inline.
 *  - Status is refreshed from the server after every action.
 *  - Permissions / tenant scope / audit are enforced by the backend.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { CircleDollarSign, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const REASON_LABEL = {
  PARTICIPANT_NOT_LINKED: 'Participant is not linked to this shift',
  NO_SERVICE_ITEM: 'No funded service item configured for this participant',
  AMBIGUOUS_PSI_FOR_SERVICE_ITEM: 'Multiple active service items match — select one manually',
  NO_ACTIVE_AGREEMENT: 'No active service agreement covers this item',
  ITEM_NOT_EFFECTIVE_ON_DATE: 'Agreement not effective on the shift date',
  NO_FUNDING_SOURCE: 'No valid funding source for the shift date',
  NO_AUTHORISED_RATE: 'No authorised rate resolves on the shift date',
  INVALID_SERVICE_RESOLUTION_MODE: 'Shift has an invalid service resolution mode',
};

export function SilBillingReadinessPanel({ shift, onChange }) {
  const { hasPermission } = useAuth();
  const canView = hasPermission('service_allocations.view');
  const canUpdate = hasPermission('service_allocations.update');

  const [enabled, setEnabled] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [reasons, setReasons] = useState({}); // allocation_id -> reason string

  const isPerParticipant = shift?.service_resolution_mode === 'PER_PARTICIPANT';

  const load = useCallback(async () => {
    if (!shift?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/shifts/${shift.id}/service-allocations/per-participant`);
      setEnabled(!!res.data?.feature_enabled);
      setRows(res.data?.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [shift?.id]);

  useEffect(() => {
    if (isPerParticipant && canView) load();
  }, [isPerParticipant, canView, load]);

  const markReady = async (row) => {
    setBusyId(row.id);
    setReasons((r) => ({ ...r, [row.id]: null }));
    try {
      const res = await api.post(`/shifts/${shift.id}/service-allocations/${row.id}/mark-ready`);
      const d = res.data?.data || {};
      if (d.ready) {
        toast.success('Allocation marked ready for billing');
        await load();
        onChange && onChange();
      } else {
        const label = REASON_LABEL[d.reason] || d.reason || 'Configuration incomplete';
        setReasons((r) => ({ ...r, [row.id]: label }));
        toast.warning(`Cannot mark ready: ${label}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = detail?.message || detail?.code || 'Failed to mark ready';
      setReasons((r) => ({ ...r, [row.id]: msg }));
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  if (!isPerParticipant || !canView || !enabled) return null;

  return (
    <Card data-testid="sil-billing-readiness-panel">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <CircleDollarSign className="w-4 h-4" /> Per-Participant Billing Readiness
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2 text-sm">
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-1">No per-participant allocations on this shift.</div>
        ) : (
          <div className="space-y-1.5">
            {rows.map((row) => {
              const ready = row.status === 'READY_FOR_BILLING';
              const pending = row.status === 'PENDING_BILLING_CONFIGURATION';
              return (
                <div
                  key={row.id}
                  className="rounded-md border bg-slate-50 p-2 flex items-start justify-between gap-2 text-xs"
                  data-testid={`sil-alloc-row-${row.id}`}
                >
                  <div className="flex-1 space-y-0.5">
                    <div className="font-medium">{row.participant_name || (row.participant_id ? `${String(row.participant_id).slice(0, 8)}…` : 'Unknown')}</div>
                    <div>
                      {ready ? (
                        <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700" data-testid={`sil-alloc-status-${row.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Ready for billing
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700" data-testid={`sil-alloc-status-${row.id}`}>
                          <Clock className="w-3 h-3 mr-1" /> Pending billing configuration
                        </Badge>
                      )}
                    </div>
                    {reasons[row.id] && (
                      <div className="flex items-start gap-1 text-[11px] text-red-600 pt-0.5" data-testid={`sil-alloc-reason-${row.id}`}>
                        <AlertTriangle className="w-3 h-3 mt-px shrink-0" /> <span>{reasons[row.id]}</span>
                      </div>
                    )}
                  </div>
                  {pending && canUpdate && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] shrink-0"
                      disabled={busyId === row.id}
                      onClick={() => markReady(row)}
                      data-testid={`sil-mark-ready-btn-${row.id}`}
                    >
                      {busyId === row.id ? 'Checking…' : 'Mark ready for billing'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {rows.length > 0 && (() => {
          const readyCount = rows.filter((r) => r.status === 'READY_FOR_BILLING').length;
          const allReady = readyCount === rows.length;
          return (
            <div
              className={`border-t pt-1.5 text-[11px] font-medium ${allReady ? 'text-emerald-700' : 'text-amber-700'}`}
              data-testid="sil-billing-readiness-footer"
            >
              {allReady
                ? `Per-participant billing — all participants ready (${readyCount}/${rows.length})`
                : `Per-participant billing — readiness required (${readyCount}/${rows.length} ready)`}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

export default SilBillingReadinessPanel;
